import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { useTranslation } from "components/translations/useTranslation";
import { Button, ButtonType } from "components/common/Button.component";
import { Tag, TagType } from "components/common/Tag.component";
import { Loader, LoaderSize } from "components/common/Loader.component";
import { SegmentedControl } from "components/common/SegmentedControl.component";
import { ISegmentedControlOption } from "types/gaiaComponents/segmentedControl";

import { getWorkflowsHistory, loadMoreWorkflowsHistory } from "components/modals/modal.actions";
import ResizableTable from "components/resizableTable/Table.component";
import { WORKFLOW_HISTORY_BASE, WORKFLOW_HISTORY_WITH_PREVIEW } from "components/resizableTable/columnsMap";
import { Tooltip } from "components/tooltip/Tooltip";
import { UserProfile } from "components/workflows/UserProfile.component";

import * as reduxSelectors from "components/store/application.reducers";

import { DOCUMENT_TYPE, LOADING_STATUS } from "utils/constants";
import { getDateTimeFormatFromLanguage } from "utils/dateFormatter";
import { generateRandom } from "utils/genereateMathRandom.function";
import { launchDarkly } from "utils/launchDarkly";

import {
    WorkflowHistoryData,
    WorkflowHistoryProps,
    WorkflowHistoryRow,
} from "types/modals";

import "components/modals/WorkflowHistory.modal.scss";

const DEFAULT_PAGE_SIZE = 20;
const TABLE_WIDTH_PADDING = 40;
const TABLE_WIDTH_MIN = 600;

const WorkflowHistory = ({
    more,
    closeModal,
    errorCallback,
}: WorkflowHistoryProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const { docTypes, ruleId } = more;

    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userLanguageCode = useSelector(reduxSelectors.getUsersCurrentLanguage);

    const [workflowHistory, setWorkflowHistory] = useState<WorkflowHistoryData | null>(null);
    const [loadingStatus, setLoadingStatus] = useState<number>(LOADING_STATUS.LOADING);
    const [activeCounter, setActiveCounter] = useState<number>(0);
    const [tableWidth, setTableWidth] = useState<number>(TABLE_WIDTH_MIN);
    const contentRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const updateWidth = () => {
            if (contentRef.current) {
                const available = contentRef.current.clientWidth - TABLE_WIDTH_PADDING;
                if (available > 0) {
                    setTableWidth(Math.max(available, TABLE_WIDTH_MIN));
                }
            }
        };
        updateWidth();

        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== "undefined" && contentRef.current) {
            observer = new ResizeObserver(updateWidth);
            observer.observe(contentRef.current);
        }
        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            observer?.disconnect();
        };
    }, []);

    const dateValueFormatter = useMemo(
        () => getDateTimeFormatFromLanguage(userLanguageCode),
        [userLanguageCode],
    );

    const isPreviewHistoryEnabled = Boolean(
        launchDarkly.userClient && launchDarkly.userClient.variation("show-workflow-history-preview", false),
    );

    const loadHistory = useCallback(
        (documentType: number) => {
            setLoadingStatus(LOADING_STATUS.LOADING);
            setWorkflowHistory(null);
            dispatch(
                getWorkflowsHistory(
                    {
                        companyId,
                        documentType: documentType || undefined,
                        ruleConfigId: ruleId || undefined,
                    },
                    (response: WorkflowHistoryData) => {
                        setWorkflowHistory(response);
                        setLoadingStatus(LOADING_STATUS.DONE);
                    },
                    (error: Record<string, any>) => {
                        setLoadingStatus(LOADING_STATUS.ERROR);
                        errorCallback(error);
                    },
                ) as any,
            );
        },
        [dispatch, companyId, ruleId, errorCallback],
    );

    useEffect(() => {
        loadHistory(0);
    }, [loadHistory]);

    const handleCounterChange = useCallback(
        (counter: number) => {
            setActiveCounter(counter);
            loadHistory(counter);
        },
        [loadHistory],
    );

    const loadMoreRecords = useCallback(() => {
        if (!workflowHistory || workflowHistory.records <= DEFAULT_PAGE_SIZE) {
            return;
        }
        dispatch(
            loadMoreWorkflowsHistory(
                {
                    companyId,
                    page: workflowHistory.page + 1,
                    rows: DEFAULT_PAGE_SIZE,
                    documentType: activeCounter > 0 ? activeCounter : undefined,
                    ruleConfigId: ruleId || undefined,
                },
                (response: WorkflowHistoryData) => {
                    setWorkflowHistory((prev) => {
                        if (!prev) return response;
                        return {
                            ...prev,
                            page: response.page,
                            rows: [...prev.rows, ...(response.rows || [])],
                        };
                    });
                },
                (error: Record<string, any>) => {
                    errorCallback(error);
                },
            ) as any,
        );
    }, [dispatch, workflowHistory, companyId, activeCounter, ruleId, errorCallback]);

    const tableRows = useMemo(() => {
        const source: Record<string, any> | null = workflowHistory ? { ...workflowHistory } : null;
        if (!source) {
            return { rows: [] } as any;
        }
        source.rows = (workflowHistory?.rows || []).map((record: WorkflowHistoryRow, index: number) => {
            const mappedRecord: Record<string, any> = { ...record };
            mappedRecord.index = index;
            mappedRecord.validFrom = moment(record.validFrom).format(dateValueFormatter);
            mappedRecord.operation = translate("popUp.workflowHistory." + record.operation);
            mappedRecord.comment = record.comment || "";
            mappedRecord.user = record.userDTO ? (
                <UserProfile user={record.userDTO as any} uniqueKey={String(index)} />
            ) : (
                ""
            );

            if (isPreviewHistoryEnabled) {
                mappedRecord.preview = record.version != null ? (
                    <Link
                        to={"/configuration/workflows/workflowHistory/" + record.idRuleConfiguration + "/" + record.version}
                        data-for={record.idRuleConfiguration + "-preview-tooltip"}
                        data-tip=""
                    >
                        <Button
                            type={ButtonType.SECONDARY}
                            onClick={(event: React.MouseEvent) => event.stopPropagation()}
                            text={translate("popUp.workflowHistory.view")}
                        />
                        <Tooltip
                            place="bottom"
                            id={record.idRuleConfiguration + "-preview-tooltip"}
                            content={() => <div>{translate("popUp.workflowHistory.preview")}</div>}
                        />
                    </Link>
                ) : (
                    ""
                );
            }

            if (record.active) {
                const randomId = generateRandom();
                mappedRecord.status = (
                    <div className="ga-tag ga-tag--utility-green">
                        <span
                            className="ga-tag__label white-space-unset"
                            data-for={"active-when-deleted-" + randomId}
                            data-tip=""
                        >
                            {translate("popUp.workflowHistory.active")}
                            {record.operation === "deleted" && (
                                <Tooltip
                                    place="bottom"
                                    id={"active-when-deleted-" + randomId}
                                    content={() => (
                                        <div>
                                            {translate("popUp.workflowHistory.activeDeleted", record.description)}
                                        </div>
                                    )}
                                />
                            )}
                        </span>
                    </div>
                );
            } else if (record.operation !== "start_history") {
                mappedRecord.status = (
                    <Tag
                        type={TagType.UTILITY_ROSE}
                        message={translate("popUp.workflowHistory.inactive")}
                    />
                );
            }

            return mappedRecord;
        });
        return source;
    }, [workflowHistory, dateValueFormatter, translate, isPreviewHistoryEnabled]);

    const selectorValues = useMemo(
        () => ({ role_select: "", documentType: "" }),
        [],
    );

    const counterOptions: ISegmentedControlOption[] | null = docTypes
        ? docTypes.map((type: number, index: number) => ({
              key: index,
              isSelected: activeCounter === docTypes[index],
              onClick: () => handleCounterChange(docTypes[index]),
              label: (
                  <>
                      <span
                          className={
                              "appicon document-type-icon-24 document-type-icon document-type-" +
                              DOCUMENT_TYPE.asString(type) +
                              "-24"
                          }
                      />
                      {translate("documentTypes." + DOCUMENT_TYPE.asString(type))}
                  </>
              )
          }))
        : null;

    return (
        <>
            <div className="ga-modal__content workflow-history-modal" ref={contentRef}>
                <div className={"pb-4 text-md"}>
                    {translate("popUp.workflowHistory.info")}
                </div>
                {counterOptions && (
                    <div className="workflow-history-modal__counters">
                        <SegmentedControl
                            options={[
                                {
                                    key: "history_all_doc_types",
                                    label: translate("popUp.workflowHistory.allDocumentType"),
                                    isSelected: activeCounter === 0,
                                    onClick: () => handleCounterChange(0)
                                },
                                ...counterOptions
                            ]}
                        />
                    </div>
                )}

                <div className="workflow-history-modal__grid-wrapper workflow_history">
                    {loadingStatus === LOADING_STATUS.LOADING && !workflowHistory ? (
                        <Loader size={LoaderSize.Medium} />
                    ) : (
                        <ResizableTable
                            tasks={tableRows}
                            hideSearchRow={true}
                            tableWidth={tableWidth}
                            defaultColumns={
                                isPreviewHistoryEnabled
                                    ? WORKFLOW_HISTORY_WITH_PREVIEW
                                    : WORKFLOW_HISTORY_BASE
                            }
                            selectors={selectorValues}
                            translate={translate}
                            filters={{ columns: {} }}
                            loadMoreTasks={loadMoreRecords}
                            loadingStatus={loadingStatus}
                        />
                    )}
                </div>
            </div>
            <div className="ga-modal__actions">
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={closeModal}
                    text={translate("popUp.close")}
                />
            </div>
        </>
    );
};

export default WorkflowHistory;
